import Footer from "@/components/Landing/Footer";
import Navbar from "@/components/Landing/Navbar";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

export default function Privacy() {
    let langedPrivacy: { [key: string]: string } = {
        en: `
1. [Introduction](#introduction)
1. [Information We Collect](#information-we-collect)
1. [How We Use Your Information](#how-we-use-your-information)
1. [How We Share Your Information](#how-we-share-your-information)
1. [Legal Compliance and Transparency](#legal-compliance-and-transparency)
1. [Business Transfers](#business-transfers)
1. [Your Choices](#your-choices)
1. [Security](#security)
1. [Changes to This Policy](#changes-to-this-policy)
1. [Contact Us](#contact-us)

# Privacy Policy

## Introduction  <a name="introduction"></a>

Welcome to Teamly. We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and share your information when you use our services.

## Information We Collect <a name="information-we-collect"></a>

### Personal Information
- **Account Information**: When you create an account, we collect your username, email address, and password.
- **Profile Information**: You may provide additional information such as a profile picture, bio, and other details.
- **Social Media**: If you choose to connect your social media account, we may collect information from those accounts.

### Usage Information
- **Messages**: We collect and store the messages you send and receive, including text, images, and other media.
- **Activity**: We collect information about your interactions with the service, such as the channels you join and the messages you read.

### Device Information
- **IP Address**: We collect your IP address when you access our services.
- **Device Details**: We collect information about the device you use to access our services, including the operating system, browser type, and device identifiers.

## How We Use Your Information <a name="how-we-use-your-information"></a>

- **To Provide Services**: We use your information to operate and maintain our services, including delivering messages and managing your account.
- **To Improve Services**: We analyze usage data to improve the functionality and performance of our services.
- **To Communicate**: We may use your email address to send you service-related notifications and updates.
- **To Ensure Security**: We use your information to detect and prevent fraud, abuse, and other harmful activities.
- **To Personalize Experience**: We use your information to personalize your experience, such as showing relevant content and recommendations.
- **To Conduct Research**: We may use your information for research and analysis to better understand our users and improve our services.
- **To Comply with Legal Obligations**: We use your information to comply with legal obligations and respond to legal requests.

## How We Share Your Information <a name="how-we-share-your-information"></a>

- **With Service Providers**: We may share your information with third-party service providers who help us operate and improve our services.
- **For Legal Reasons**: We may disclose your information if required by law or in response to a legal request.
- **With Your Consent**: We may share your information with your consent or at your direction.

## Legal Compliance and Due Diligence

We adhere to applicable laws and regulations regarding data handling during the sale or transfer of our platform. This includes:

- **Regulatory Approvals**: Ensuring compliance with jurisdictional laws for user data handling.
- **User Notification**: Informing users about their rights and any changes in service continuity.

## Business Transfers <a name="business-transfers"></a>

In the event of a merger, acquisition, sale, or transfer of all or a portion of our assets, your personal information may be transferred as part of the business transaction. Such transfers are essential for maintaining service continuity and ensuring that new owners adhere to existing privacy standards. You will be notified of:

- **Ownership Changes**: Any changes in ownership via email and/or a prominent notice on our website.
- **Policy Adjustments**: Adjustments to how your data is used, including additional user controls to manage or delete your information post-transfer.

## Your Choices <a name="your-choices"></a>

- **Account Settings**: You can update your account information and preferences through your account settings.
- **Data Access and Deletion**: You can request access to or deletion of your personal information by contacting us.

## Security <a name="security"></a>

We take reasonable measures to protect your information from unauthorized access, use, or disclosure. However, no internet-based service is completely secure, and we cannot guarantee the absolute security of your information.

## Changes to This Policy <a name="changes-to-this-policy"></a>

We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on our website and updating the effective date.

## Contact Us <a name="contact-us"></a>

If you have any questions or concerns about this Privacy Policy, please contact us at [contact@teamly.one](mailto:contact@teamly.one).`,
    tr: `
1. [Giriş](#giriş)
2. [Topladığımız Bilgiler](#topladığımız-bilgiler)
3. [Bilgilerinizi Nasıl Kullanıyoruz](#bilgilerinizi-nasıl-kullanıyoruz)
4. [Bilgilerinizi Nasıl Paylaşıyoruz](#bilgilerinizi-nasıl-paylaşıyoruz)
5. [Yasal Uyum ve Şeffaflık](#yasal-uyum-ve-şeffaflık)
5. [İş Devirleri](#iş-devirleri)
5. [Seçimleriniz](#seçimleriniz)
6. [Güvenlik](#güvenlik)
7. [Bu Politikada Değişiklikler](#bu-politikada-değişiklikler)
8. [Bize Ulaşın](#bize-ulaşın)

# Gizlilik Politikası

## Giriş  <a name="giriş"></a>

[Chat Websitesi]’ne hoş geldiniz. Gizliliğinizi önemsiyor ve kişisel bilgilerinizi koruma taahhüdünde bulunuyoruz. Bu Gizlilik Politikası, hizmetlerimizi kullandığınızda bilgilerinizi nasıl topladığımızı, kullandığımızı ve paylaştığımızı açıklar.

## Topladığımız Bilgiler <a name="topladığımız-bilgiler"></a>

### Kişisel Bilgiler
- **Hesap Bilgileri**: Bir hesap oluşturduğunuzda, kullanıcı adınızı, e-posta adresinizi ve şifrenizi toplarız.
- **Profil Bilgileri**: Ek bilgiler sağlayabilirsiniz, örneğin profil resmi, bio ve diğer detaylar.
- **Sosyal Medya**: Sosyal medya hesabınızı bağlamayı seçerseniz, bu hesaplardan bilgi toplayabiliriz.

### Kullanım Bilgileri
- **Mesajlar**: Gönderdiğiniz ve aldığınız mesajları, metin, resim ve diğer medyaları toplar ve saklarız.
- **Aktivite**: Hizmetle etkileşimleriniz hakkında bilgileri toplarız, örneğin katıldığınız kanallar ve okuduğunuz mesajlar.

### Cihaz Bilgileri
- **IP Adresi**: Hizmetlerimize eriştiğinizde IP adresinizi toplarız.
- **Cihaz Detayları**: Hizmetlerimize erişmek için kullandığınız cihaz hakkında bilgileri toplarız, işletim sistemi, tarayıcı türü ve cihaz kimlikleri dahil.

## Bilgilerinizi Nasıl Kullanıyoruz <a name="bilgilerinizi-nasıl-kullanıyoruz"></a>

- **Hizmet Sağlamak İçin**: Bilgilerinizi hizmetlerimizi
- **Geliştirmek İçin**: Kullanım verilerini analiz ederek hizmetlerimizin işlevselliğini ve performansını iyileştiririz.
- **İletişim Kurmak İçin**: E-posta adresinizi hizmetle ilgili bildirimler ve güncellemeler göndermek için kullanabiliriz.
- **Güvenliği Sağlamak İçin**: Bilgilerinizi sahtekarlık, kötüye kullanım ve diğer zararlı faaliyetleri tespit etmek ve önlemek için kullanırız.
- **Deneyimi Kişiselleştirmek İçin**: İçeriği ve önerileri göstermek gibi deneyiminizi kişiselleştirmek için bilgilerinizi kullanırız.
- **Araştırma Yapmak İçin**: Kullanıcılarımızı daha iyi anlamak ve hizmetlerimizi iyileştirmek için bilgiler
- **Yasal Yükümlülüklere Uymak İçin**: Yasal yükümlülüklere uymak ve yasal taleplere yanıt vermek için bilgilerinizi kullanırız.

## Bilgilerinizi Nasıl Paylaşıyoruz <a name="bilgilerinizi-nasıl-paylaşıyoruz"></a>

- **Hizmet Sağlayıcılarla**: Hizmetlerimizi işletmemize ve iyileştirmemize yardımcı olan üçüncü taraf hizmet sağlayıcılarla bilgilerinizi paylaşabiliriz.
- **Yasal Nedenlerle**: Yasal bir zorunluluk veya yasal bir talep karşısında bilgilerinizi açıklayabiliriz.
- **Onayınızla**: Bilgilerinizi onayınızla veya yönlendirmeniz doğrultusunda paylaşabiliriz.

## Yasal Uyum ve Şeffaflık <a name="yasal-uyum-ve-şeffaflık"></a>

- **Yasal Gereklilikler**: Yürürlükteki yasalara uygun şekilde veri işlenir.
- **Kullanıcı Hakları**: Kullanıcılar, bilgilerine erişim, düzeltme ve silme haklarını korur.

## İş Devirleri <a name="iş-devirleri"></a>

Birleşme, devralma veya varlıkların tamamen ya da kısmen satışı durumunda kişisel bilgileriniz işlemin bir parçası olarak devredilebilir. Bu tür durumlar şunları içerir:

- **Sahiplik Değişiklikleri**: Sahiplik değişiklikleri durumunda, kullanıcılar e-posta veya web sitesi duyurusu yoluyla bilgilendirilir.
- **Politika Güncellemeleri**: Bilgilerinizin kullanımıyla ilgili yeni kontroller ve seçenekler sunulur.

## Seçimler
- **Hesap Ayarları**: Hesap bilgilerinizi ve tercihlerinizi hesap ayarları aracılığıyla güncelleyebilirsiniz.
- **Veri Erişimi ve Silme**: Kişisel bilgilerinize erişim veya silme talebinde bulunarak bize ulaşabilirsiniz.

## Güvenlik <a name="güvenlik"></a>

Bilgilerinizi yetkisiz erişime, kullanıma veya açıklamaya karşı korumak için makul önlemler alırız. Ancak, hiçbir internet tabanlı hizmet tamamen güvenli değildir ve bilgilerinizin mutlak güvenliğini garanti edemeyiz.

## Bu Politikada Değişiklikler <a name="bu-politikada-değişiklikler"></a>

Bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Yeni politikayı web sitemizde yayınlayarak ve etkinlik tarihini güncelleyerek önemli değişiklikler hakkında sizi bilgilendiririz.

## Bize Ulaşın <a name="bize-ulaşın"></a>

Bu Gizlilik Politikası ile ilgili sorularınız veya endişeleriniz varsa, lütfen bizimle iletişime geçin: [contact@teamly.one](mailto:contact@teamly.one).`,
};

    const [lang, setLang] = useState("en");
    
    return (
        <>
            <Navbar />
            <div className="sm:px-24">
                <div className="w-full flex flex-col items-center justify-center">
                    <div className="flex items-center justify-end w-full p-4">
                        <button onClick={() => setLang("en")} className={`p-2 text-white ${lang === "en" ? "bg-brand-dark" : "bg-brand-light"} rounded-lg`}>English</button>
                        <button onClick={() => setLang("tr")} className={`p-2 text-white ${lang === "tr" ? "bg-brand-dark" : "bg-brand-light"} rounded-lg ml-2`}>Türkçe</button>
                    </div>
                </div>
                <ReactMarkdown               
                    className="prose prose-lg max-w-none w-full p-2 sm:p-16"
                    rehypePlugins={[rehypeRaw]}
                    components={{
                        a: ({ node, ...props }) => <a {...props} className="text-blue-500 underline" />,
                        h1: ({ node, ...props }) => <h1 {...props} className="text-2xl font-bold text-white" />,
                        h2: ({ node, ...props }) => <h2 {...props} className="text-xl font-bold text-white" />,
                        h3: ({ node, ...props }) => <h3 {...props} className="text-lg font-bold text-white" />,
                        h4: ({ node, ...props }) => <h4 {...props} className="text-base font-bold text-white" />,
                        h5: ({ node, ...props }) => <h5 {...props} className="text-sm font-bold text-white" />,
                        h6: ({ node, ...props }) => <h6 {...props} className="text-xs font-bold text-white" />,
                        p: ({ node, ...props }) => <p {...props} className="text-white" />,
                        ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-12" />,
                        ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-4" />,
                        li: ({ node, ...props }) => <li {...props} className="text-white" />,
                        blockquote: ({ node, ...props }) => <blockquote {...props} className="border-l-4 text-white pl-2" />,
                        code: ({ node, ...props }) => <code {...props} className="bg-gray-200 text-white p-1 rounded-md" />,
                        pre: ({ node, ...props }) => <pre {...props} className="bg-gray-200 text-white p-1 rounded-md" />,
                        strong: ({ node, ...props }) => <strong {...props} className="font-bold text-white" />,
                        em: ({ node, ...props }) => <em {...props} className="italic text-white" />,
                    }}
                >
                    {langedPrivacy[lang]}
                </ReactMarkdown>
            </div>

            <Footer />
        </>
    )
}